import React, { useEffect, useState } from "react";
import { FiSearch, FiUsers, FiClock, FiActivity, FiMail, FiMessageSquare, FiHome, FiLock } from "react-icons/fi";
import "./AdminPanel.css";

export default function AdminPanel() {
  const [isAuthorized, setIsAuthorized] = useState(() => {
    return sessionStorage.getItem("admin_auth") === "true";
  });
  const [emailInput, setEmailInput] = useState("");
  const [passwordInput, setPasswordInput] = useState("");
  const [loginError, setLoginError] = useState("");

  const [activeTab, setActiveTab] = useState("users"); // "users" or "tickets"
  const [users, setUsers] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [loadingTickets, setLoadingTickets] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:5000";

  // Fetch Users (only if authorized)
  useEffect(() => {
    if (!isAuthorized) return;
    fetch(`${API_BASE}/api/auth/admin/users`)
      .then((res) => res.json())
      .then((data) => {
        if (data.users) setUsers(data.users);
        setLoadingUsers(false);
      })
      .catch((err) => {
        console.error(err);
        setLoadingUsers(false);
      });
  }, [API_BASE, isAuthorized]);

  // Fetch Tickets (only if authorized)
  useEffect(() => {
    if (!isAuthorized) return;
    fetch(`${API_BASE}/api/auth/admin/support`)
      .then((res) => res.json())
      .then((data) => {
        if (data.tickets) setTickets(data.tickets);
        setLoadingTickets(false);
      })
      .catch((err) => {
        console.error(err);
        setLoadingTickets(false);
      });
  }, [API_BASE, activeTab, isAuthorized]);

  const handleAdminLogin = (e) => {
    e.preventDefault();
    const adminEmail = process.env.REACT_APP_ADMIN_EMAIL || "admin@example.com";
    const adminPassword = process.env.REACT_APP_ADMIN_PASSWORD || "admin123";
    if (emailInput === adminEmail && passwordInput === adminPassword) {
      setIsAuthorized(true);
      sessionStorage.setItem("admin_auth", "true");
      setLoginError("");
    } else {
      setLoginError("Access Denied: Invalid Admin Credentials.");
    }
  };

  const handleLogout = () => {
    setIsAuthorized(false);
    sessionStorage.removeItem("admin_auth");
  };

  if (!isAuthorized) {
    return (
      <div className="admin-login-container">
        <div className="admin-login-glass">
          <div className="admin-login-header">
            <div className="admin-logo-circle">FF</div>
            <h2>VoiceForge Admin</h2>
            <p>Authorized access only. Enter administrative keys.</p>
          </div>
          
          {loginError && <div className="admin-login-error">{loginError}</div>}
          
          <form onSubmit={handleAdminLogin} className="admin-login-form">
            <div className="form-group">
              <label>Admin Email</label>
              <div className="input-with-icon">
                <FiMail className="input-icon" />
                <input 
                  type="email" 
                  required 
                  value={emailInput} 
                  onChange={e => setEmailInput(e.target.value)} 
                  placeholder="fahadavice@gmail.com"
                />
              </div>
            </div>
            
            <div className="form-group">
              <label>Password</label>
              <div className="input-with-icon">
                <FiLock className="input-icon" />
                <input 
                  type="password" 
                  required 
                  value={passwordInput} 
                  onChange={e => setPasswordInput(e.target.value)} 
                  placeholder="••••••••"
                />
              </div>
            </div>
            
            <button type="submit" className="admin-login-btn">
              Authenticate Dashboard
            </button>
          </form>
          
          <button 
            className="admin-login-back" 
            onClick={() => window.history.pushState(null, "", "/") || window.dispatchEvent(new PopStateEvent('popstate'))}
          >
            ← Back to Home
          </button>
        </div>
      </div>
    );
  }

  const filteredUsers = users.filter(user => 
    user.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredTickets = tickets.filter(ticket => 
    ticket.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    ticket.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    ticket.message?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Stats calculation
  const totalUsers = users.length;
  const newUsersToday = users.filter(user => {
    const joinedDate = new Date(user.createdAt);
    const today = new Date();
    return joinedDate.toDateString() === today.toDateString();
  }).length;

  const totalTickets = tickets.length;

  const getInitials = (name) => {
    if (!name) return "?";
    return name.split(" ").map(n => n[0]).join("").toUpperCase().substring(0, 2);
  };

  const getRandomColor = (name) => {
    const colors = ["#8B5CF6", "#3B82F6", "#10B981", "#F59E0B", "#EF4444", "#EC4899"];
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    const index = Math.abs(hash) % colors.length;
    return colors[index];
  };

  return (
    <div className="admin-container">
      <div className="admin-layout-wrapper">
        
        {/* Left Sidebar Navigation */}
        <aside className="admin-sidebar">
          <div className="admin-sidebar-header">
            <div className="admin-logo-circle">FF</div>
            <div>
              <h3>VoiceForge</h3>
              <p>Control Center</p>
            </div>
          </div>

          <nav className="admin-nav-menu">
            <button 
              className={`admin-nav-item ${activeTab === "users" ? "active" : ""}`}
              onClick={() => { setActiveTab("users"); setSearchQuery(""); }}
            >
              <FiUsers size={18} />
              <span>Users Directory</span>
            </button>

            <button 
              className={`admin-nav-item ${activeTab === "tickets" ? "active" : ""}`}
              onClick={() => { setActiveTab("tickets"); setSearchQuery(""); }}
            >
              <FiMessageSquare size={18} />
              <span>Support Tickets</span>
              {tickets.length > 0 && (
                <span className="nav-badge-count">{tickets.length}</span>
              )}
            </button>
          </nav>

          <div className="admin-sidebar-footer">
            <button className="admin-nav-item logout-btn" onClick={handleLogout} style={{marginBottom: 8}}>
              <FiLock size={18} />
              <span>Lock Console</span>
            </button>
            <button className="admin-nav-item exit-btn" onClick={() => window.history.pushState(null, "", "/") || window.dispatchEvent(new PopStateEvent('popstate'))}>
              <FiHome size={18} />
              <span>Exit to Home</span>
            </button>
          </div>
        </aside>

        {/* Right Content Panel */}
        <main className="admin-main-panel">
          
          {/* Header */}
          <div className="admin-panel-header">
            <div>
              <h1 className="admin-title">
                {activeTab === "users" ? "Users Directory" : "Support Messages"}
              </h1>
              <p className="admin-subtitle">
                {activeTab === "users" 
                  ? "Manage and monitor registered platform accounts" 
                  : "Review incoming feedback and help requests from users"}
              </p>
            </div>
            <div className="admin-badge-live">
              <span className="live-dot"></span>
              LIVE SYSTEM
            </div>
          </div>

          {/* Stats Bar */}
          <div className="admin-stats-grid">
            <div className="stat-card">
              <div className="stat-icon-wrapper purple">
                <FiUsers size={20} />
              </div>
              <div className="stat-info">
                <h3>{totalUsers}</h3>
                <p>Registered Users</p>
              </div>
            </div>
            
            <div className="stat-card">
              <div className="stat-icon-wrapper green">
                <FiMessageSquare size={20} />
              </div>
              <div className="stat-info">
                <h3>{totalTickets}</h3>
                <p>Support Tickets</p>
              </div>
            </div>

            <div className="stat-card">
              <div className="stat-icon-wrapper blue">
                <FiActivity size={20} />
              </div>
              <div className="stat-info">
                <h3>{newUsersToday}</h3>
                <p>New Signups Today</p>
              </div>
            </div>
          </div>

          {/* Filter Bar */}
          <div className="admin-actions-bar">
            <div className="search-box-wrapper">
              <FiSearch className="search-icon" />
              <input 
                type="text" 
                placeholder={activeTab === "users" ? "Search by name or email..." : "Search tickets..."} 
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="admin-search-input"
              />
            </div>
            <div className="user-count-badge">
              {activeTab === "users" 
                ? `Showing ${filteredUsers.length} of ${totalUsers} users`
                : `Showing ${filteredTickets.length} of ${totalTickets} tickets`}
            </div>
          </div>

          {/* Main List Render */}
          {activeTab === "users" ? (
            loadingUsers ? (
              <div className="loader-container">
                <div className="spinner"></div>
                <p>Fetching user directory...</p>
              </div>
            ) : (
              <div className="table-responsive">
                <table className="users-table">
                  <thead>
                    <tr>
                      <th>User Profile</th>
                      <th>Email Address</th>
                      <th>Joined Date</th>
                      <th style={{ textAlign: "right" }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map((user, idx) => (
                      <tr key={idx} className="table-row-animate">
                        <td>
                          <div className="user-profile-cell">
                            <div 
                              className="user-avatar" 
                              style={{ backgroundColor: getRandomColor(user.name || "User") }}
                            >
                              {getInitials(user.name || "User")}
                            </div>
                            <div className="user-profile-info">
                              <span className="user-name-text">{user.name}</span>
                            </div>
                          </div>
                        </td>
                        <td>
                          <div className="email-cell">
                            <FiMail className="cell-icon" />
                            <span>{user.email}</span>
                          </div>
                        </td>
                        <td>
                          <span className="joined-date-text">
                            {new Date(user.createdAt).toLocaleDateString("en-US", {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric'
                            })}
                          </span>
                        </td>
                        <td style={{ textAlign: "right" }}>
                          <span className="status-pill active-status">Active</span>
                        </td>
                      </tr>
                    ))}
                    {filteredUsers.length === 0 && (
                      <tr>
                        <td colSpan="4" style={{ textAlign: "center", padding: "40px 20px" }}>
                          <div className="no-users-view">
                            <FiUsers size={40} style={{ opacity: 0.3, marginBottom: "10px" }} />
                            <p style={{ color: "#9090a8" }}>No registered users match your search criteria.</p>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )
          ) : (
            // Support Tickets rendering
            loadingTickets ? (
              <div className="loader-container">
                <div className="spinner"></div>
                <p>Loading support messages...</p>
              </div>
            ) : (
              <div className="tickets-feed-container">
                {filteredTickets.map((ticket, idx) => (
                  <div key={idx} className="ticket-card-item">
                    <div className="ticket-card-header">
                      <div className="ticket-sender-profile">
                        <div 
                          className="user-avatar ticket-av"
                          style={{ backgroundColor: getRandomColor(ticket.name || "Sender") }}
                        >
                          {getInitials(ticket.name || "Sender")}
                        </div>
                        <div>
                          <h4 className="ticket-sender-name">{ticket.name}</h4>
                          <span className="ticket-sender-email">{ticket.email}</span>
                        </div>
                      </div>
                      <div className="ticket-meta-info">
                        <FiClock size={12} style={{ marginRight: 4 }} />
                        <span>
                          {new Date(ticket.createdAt).toLocaleString("en-US", {
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </span>
                      </div>
                    </div>
                    <div className="ticket-message-body">
                      <p>{ticket.message}</p>
                    </div>
                  </div>
                ))}
                {filteredTickets.length === 0 && (
                  <div className="no-users-view" style={{ padding: "60px 20px" }}>
                    <FiMessageSquare size={40} style={{ opacity: 0.3, marginBottom: "10px" }} />
                    <p style={{ color: "#9090a8" }}>No support messages found.</p>
                  </div>
                )}
              </div>
            )
          )}
        </main>
      </div>
    </div>
  );
}
